/**
 * MusicPlayer 手势与 UX：滚动穿透拦截、设置面板点击外部/ESC
 * 抽离自 MusicPlayer.js
 */
import { useEffect, useRef } from 'react';

function getScrollableAncestorWithinPlayer(playerRef, startEl) {
  try {
    const root = playerRef?.current;
    let el = startEl;
    while (el && el !== root) {
      if (el instanceof HTMLElement) {
        const style = window.getComputedStyle(el);
        const oy = (style.overflowY || '').toLowerCase();
        const canScrollY = (oy === 'auto' || oy === 'scroll') && (el.scrollHeight > el.clientHeight + 1);
        if (canScrollY) return el;
      }
      el = el && el.parentElement ? el.parentElement : null;
    }
    if (root && root instanceof HTMLElement) {
      const style = window.getComputedStyle(root);
      const oy = (style.overflowY || '').toLowerCase();
      const canScrollY = (oy === 'auto' || oy === 'scroll') && (root.scrollHeight > root.clientHeight + 1);
      if (canScrollY) return root;
    }
  } catch (_) {}
  return null;
}

/**
 * 注册滚动穿透拦截与设置面板点击外部/ESC 关闭
 * @param {{ playerRef, settingsPanelRef, settingsButtonRef, showSettings, setShowSettings, showLyrics, showPlaylist, isFullscreen, currentMusicId }} opts
 */
export function usePlayerGestures(opts) {
  const {
    playerRef,
    settingsPanelRef,
    settingsButtonRef,
    showSettings,
    setShowSettings,
    showLyrics,
    showPlaylist,
    isFullscreen,
    currentMusicId
  } = opts;

  // 用原生 wheel 事件（passive:false）拦截“滚动穿透到主页”
  useEffect(() => {
    const root = playerRef?.current;
    if (!root) return;

    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

    const onWheel = (ev) => {
      try {
        if (!root.contains(ev.target)) return;
        const scrollEl = getScrollableAncestorWithinPlayer(playerRef, ev.target);
        ev.preventDefault();
        ev.stopPropagation();
        if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
        if (!scrollEl) return;
        let dy = Number(ev.deltaY || 0);
        if (!dy) return;
        if (ev.deltaMode === 1) dy *= 16;
        else if (ev.deltaMode === 2) dy *= (scrollEl.clientHeight || 300);
        const maxTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
        const nextTop = clamp(scrollEl.scrollTop + dy, 0, maxTop);
        scrollEl.scrollTop = nextTop;
      } catch (_) {}
    };

    root.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => {
      try {
        root.removeEventListener('wheel', onWheel, { capture: true });
      } catch (_) {
        try {
          root.removeEventListener('wheel', onWheel, true);
        } catch (_) {}
      }
    };
  }, [playerRef, currentMusicId, showLyrics, showPlaylist, showSettings, isFullscreen]);

  // 设置面板：点击外部/按 ESC 关闭
  const ignoreNextMouseDownForSettingsRef = useRef(false);
  useEffect(() => {
    if (!showSettings) return;

    const onDocTouchStart = (e) => {
      const panel = settingsPanelRef?.current;
      const btn = settingsButtonRef?.current;
      const target = e.targetTouches?.[0]
        ? document.elementFromPoint(e.targetTouches[0].clientX, e.targetTouches[0].clientY)
        : e.target;
      if (!target) return;
      if (btn && btn.contains(target)) ignoreNextMouseDownForSettingsRef.current = true;
      if (panel && panel.contains(target)) ignoreNextMouseDownForSettingsRef.current = true;
    };

    const onDocMouseDown = (e) => {
      if (ignoreNextMouseDownForSettingsRef.current) {
        ignoreNextMouseDownForSettingsRef.current = false;
        return;
      }
      const panel = settingsPanelRef?.current;
      const btn = settingsButtonRef?.current;
      if (!panel) return;
      if (btn && btn.contains(e.target)) return;
      if (panel.contains(e.target)) return;
      setShowSettings(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowSettings(false);
    };

    document.addEventListener('touchstart', onDocTouchStart, { passive: true });
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('touchstart', onDocTouchStart);
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showSettings, setShowSettings, settingsPanelRef, settingsButtonRef]);
}
